/**
 * Admin/Manager sidebar navigation.
 * - Desktop (≥lg): fixed sidebar panel
 * - Mobile (<lg): drawer triggered by hamburger in topbar
 * - Menu "Cơ sở" only visible for role=admin
 * - Leaves slots for Phase 2 items (Classes, Students, Enrollments, etc.)
 */
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  BookOpen,
  Building2,
  DoorOpen,
  GraduationCap,
  LayoutDashboard,
  Users,
  UserCog,
  ClipboardList,
  BarChart2,
  ScrollText,
} from 'lucide-react';
import { BranchSwitcher } from '@client/components/branch-switcher';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { to: '/admin', label: 'Tổng quan', icon: LayoutDashboard },
  { to: '/admin/branches', label: 'Cơ sở', icon: Building2, adminOnly: true },
  { to: '/admin/rooms', label: 'Phòng học', icon: DoorOpen },
  { to: '/admin/courses', label: 'Khóa học', icon: BookOpen },
  { to: '/admin/classes', label: 'Lớp học', icon: GraduationCap },
  { to: '/admin/teachers', label: 'Giáo viên', icon: UserCog },
  { to: '/admin/students', label: 'Học viên', icon: Users },
  { to: '/admin/enrollments', label: 'Ghi danh', icon: ClipboardList },
  { to: '/admin/reports', label: 'Báo cáo', icon: BarChart2 },
  { to: '/admin/audit-log', label: 'Nhật ký', icon: ScrollText, adminOnly: true },
];

interface AppSidebarProps {
  role: string;
  fullName: string;
}

export function AppSidebar({ role, fullName: _fullName }: AppSidebarProps) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const visibleItems = navItems.filter(
    (item) => !item.adminOnly || role === 'admin',
  );

  return (
    <Sidebar
      className="border-r-0"
      style={{ '--sidebar-width': '16rem' } as React.CSSProperties}
    >
      {/* Logo + brand */}
      <SidebarHeader className="px-4 py-4 bg-sidebar">
        <Link to="/admin" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary shrink-0">
            <span className="text-xs font-bold text-primary-foreground">CLC</span>
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-sidebar-foreground truncate">
              CLC Education
            </span>
            <span className="text-xs text-sidebar-foreground/60 truncate">LMS</span>
          </div>
        </Link>
      </SidebarHeader>

      {/* Branch switcher — admin only */}
      <div className="bg-sidebar">
        <BranchSwitcher role={role} />
      </div>

      <SidebarContent className="bg-sidebar px-2">
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/50 text-xs uppercase tracking-widest px-2 py-1">
            Quản lý
          </SidebarGroupLabel>
          <SidebarMenu>
            {visibleItems.map((item) => {
              const isActive =
                item.to === '/admin'
                  ? currentPath === '/admin'
                  : currentPath.startsWith(item.to);
              return (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive}
                    className={
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground'
                    }
                  >
                    <Link to={item.to} className="flex items-center gap-2">
                      <item.icon
                        className={`h-4 w-4 shrink-0 ${isActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/70'}`}
                      />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="bg-sidebar border-t border-sidebar-border px-4 py-3">
        <p className="text-xs text-sidebar-foreground/40 text-center">
          CLC Education LMS · v1.0
        </p>
      </SidebarFooter>
    </Sidebar>
  );
}
